import '.././pages.css';
import './SdkPerformance.css';
import { useState, useEffect, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import useSdkPerformanceSummary from '../../hooks/useSdkPerformanceSummary';
import {
  PLATFORM_OPTIONS,
  PLATFORM_LABELS,
  KPIS,
  DEVICE_ALL,
  SDK_VERSION_ALL,
  SDK_RUN_ALL,
  NOT_MEASURED_ON_DEVICE,
} from './sdk-performance/constants';
import { helpText, Help } from './sdk-performance/help';
import { displayDeviceLabel } from './sdk-performance/format';
import {
  sharedIosBaselines,
  usualRange,
  verdictOf,
  runsBefore,
  visiblePlatformMetrics,
  runMetrics,
  runCountOf,
  runOptions,
  deviceOptions,
  sdkVersionOptions,
  isReleaseRun,
  summarizeMetrics,
  figureDevices,
  sameDevices,
  trendEntries,
} from './sdk-performance/metrics';
import { KpiCard, Takeaway, RunContext } from './sdk-performance/KpiCards';
import { CostTrend } from './sdk-performance/CostTrend';
import { SessionReplayTrend } from './sdk-performance/SessionReplayTrend';
import { RunHistory } from './sdk-performance/RunHistory';

function SdkPerformance() {
  const [selectedPlatform, setSelectedPlatform] = useState('android');
  const [selectedDevice, setSelectedDevice] = useState(DEVICE_ALL);
  const [selectedSdkVersion, setSelectedSdkVersion] = useState(SDK_VERSION_ALL);
  const [selectedRun, setSelectedRun] = useState(SDK_RUN_ALL);
  const [runCount, setRunCount] = useState(0);
  const [trendKey, setTrendKey] = useState('cpu');
  const [showRaw, setShowRaw] = useState(false);
  const allRepeats = useSdkPerformanceSummary(24, 'schedule', SDK_RUN_ALL, 'nightly');
  const oneRepeat = useSdkPerformanceSummary(24, 'schedule', selectedRun === SDK_RUN_ALL ? null : selectedRun, 'nightly');
  const summary = selectedRun === SDK_RUN_ALL ? allRepeats : oneRepeat;
  const loading = allRepeats.loading || summary.loading;
  const error = allRepeats.error || summary.error;
  const refetch = () => {
    allRepeats.refetch();
    if (selectedRun !== SDK_RUN_ALL) oneRepeat.refetch();
  };

  const performance = summary.data?.performance;
  const history = useMemo(() => (performance?.history || []).filter((run) => !isReleaseRun(run)), [performance?.history]);
  const metrics = useMemo(() => {
    const runIds = new Set(history.map((run) => run.id));
    return [
      ...(performance?.android?.metrics || []),
      ...(performance?.ios?.metrics || []),
      ...sharedIosBaselines(performance?.ios?.metrics, selectedRun === SDK_RUN_ALL ? null : allRepeats.data?.performance?.ios?.metrics),
    ].filter((metric) => runIds.has(metric.runId));
  }, [history, performance?.android?.metrics, performance?.ios?.metrics, selectedRun, allRepeats.data?.performance?.ios?.metrics]);
  const devices = useMemo(() => deviceOptions(metrics, selectedPlatform), [metrics, selectedPlatform]);
  const sdkVersions = useMemo(() => sdkVersionOptions(metrics, selectedPlatform, selectedDevice), [metrics, selectedPlatform, selectedDevice]);
  const repeats = runOptions(runCount);
  const kpis = useMemo(() => KPIS[selectedPlatform].filter((kpi) => (
    !kpi.optional || metrics.some((metric) => metric.platform === selectedPlatform && kpi.reports(metric))
  )), [metrics, selectedPlatform]);
  const trendKpi = kpis.find((kpi) => kpi.key === trendKey) || kpis[0];

  // Newest first: the runs that reported a figure through the page filters.
  const reportedRuns = useMemo(() => (
    history.filter((run) => run[selectedPlatform]?.available && runMetrics(metrics, run.id, selectedPlatform, selectedDevice, selectedSdkVersion).length)
  ), [history, metrics, selectedPlatform, selectedDevice, selectedSdkVersion]);
  const summaries = useMemo(() => new Map(reportedRuns.map((run) => (
    [run.id, summarizeMetrics(runMetrics(metrics, run.id, selectedPlatform, selectedDevice, selectedSdkVersion))]
  ))), [reportedRuns, metrics, selectedPlatform, selectedDevice, selectedSdkVersion]);
  const latestRun = reportedRuns[0] || null;
  const latestSummary = latestRun ? summaries.get(latestRun.id) : null;
  const baseline = useMemo(() => (latestRun ? runsBefore(reportedRuns, latestRun) : []), [reportedRuns, latestRun]);
  const rows = kpis.map((kpi) => {
    const value = kpi.read(latestSummary);
    const range = usualRange(baseline
      .filter((run) => sameDevices(kpi, summaries.get(run.id), latestSummary))
      .map((run) => kpi.read(summaries.get(run.id))));
    return { kpi, value, range, verdict: verdictOf(value, range) };
  });
  const entries = useMemo(() => trendEntries(reportedRuns, summaries), [reportedRuns, summaries]);
  // A device that never reported a figure in the loaded runs does not measure it.
  const notMeasuredOnDevice = (reports) => (
    reports && selectedDevice !== DEVICE_ALL && !visiblePlatformMetrics(metrics, selectedPlatform, selectedDevice).some(reports)
      ? NOT_MEASURED_ON_DEVICE
      : null
  );

  useEffect(() => {
    if (selectedDevice !== DEVICE_ALL && !devices.includes(selectedDevice)) {
      setSelectedDevice(DEVICE_ALL);
    }
  }, [devices, selectedDevice]);

  useEffect(() => {
    if (selectedSdkVersion !== SDK_VERSION_ALL && !sdkVersions.includes(selectedSdkVersion)) {
      setSelectedSdkVersion(SDK_VERSION_ALL);
    }
  }, [sdkVersions, selectedSdkVersion]);

  useEffect(() => {
    setSelectedDevice(DEVICE_ALL);
    setSelectedSdkVersion(SDK_VERSION_ALL);
    setTrendKey('cpu');
  }, [selectedPlatform]);

  useEffect(() => {
    setSelectedSdkVersion(SDK_VERSION_ALL);
  }, [selectedDevice]);

  useEffect(() => {
    setSelectedRun(SDK_RUN_ALL);
  }, [selectedPlatform, selectedDevice, selectedSdkVersion]);

  useEffect(() => {
    if (selectedRun === SDK_RUN_ALL && !loading) {
      setRunCount(runCountOf(visiblePlatformMetrics(metrics, selectedPlatform, selectedDevice, selectedSdkVersion)));
    }
  }, [selectedRun, loading, metrics, selectedPlatform, selectedDevice, selectedSdkVersion]);

  const platformDeviceCount = latestSummary?.devices?.length || 0;

  return (
    <div className="application-container">
      <div className="sdkp-page">
        <div className="sdkp-header">
          <h1 className="sdkp-title">SDK Performance</h1>
          <div className="sdkp-header-actions">
            <div className="sdkp-segmented" aria-label="Platform filter">
              {PLATFORM_OPTIONS.map((platform) => (
                <button
                  key={platform}
                  type="button"
                  className={selectedPlatform === platform ? 'sdkp-active' : ''}
                  onClick={() => setSelectedPlatform(platform)}
                >
                  {PLATFORM_LABELS[platform]}
                </button>
              ))}
            </div>
            <select
              className="sdkp-select"
              aria-label="Device filter"
              value={selectedDevice}
              onChange={(event) => setSelectedDevice(event.target.value)}
              disabled={!devices.length}
            >
              <option value={DEVICE_ALL}>All devices</option>
              {devices.map((device) => (
                <option key={device} value={device}>{displayDeviceLabel(device)}</option>
              ))}
            </select>
            <select
              className="sdkp-select sdkp-select-wide"
              aria-label="SDK version filter"
              value={selectedSdkVersion}
              onChange={(event) => setSelectedSdkVersion(event.target.value)}
              disabled={!sdkVersions.length}
            >
              <option value={SDK_VERSION_ALL}>All SDK versions</option>
              {sdkVersions.map((sdkVersion) => (
                <option key={sdkVersion} value={sdkVersion}>SDK {sdkVersion}</option>
              ))}
            </select>
            <span className="sdkp-filter-with-help">
              <select
                className="sdkp-select sdkp-select-wide"
                aria-label="Run filter"
                value={selectedRun}
                onChange={(event) => setSelectedRun(event.target.value)}
                disabled={!repeats.length}
              >
                <option value={SDK_RUN_ALL}>{repeats.length ? `All ${repeats.length} runs (average)` : 'All runs'}</option>
                {repeats.map((repeat) => (
                  <option key={repeat} value={repeat}>Run {repeat}</option>
                ))}
              </select>
              {repeats.length > 0 && <Help text={helpText('repeats', selectedPlatform, { runCount: repeats.length })} placement="below" align="right" />}
            </span>
            <button type="button" className="sdkp-link-button" onClick={refetch} title="Refresh" disabled={loading}>
              <RefreshCw size={15} />
            </button>
          </div>
          <p className="sdkp-subtitle">How much extra CPU, memory and network the Contentsquare SDK adds to our test app. Nightly scheduled runs only.</p>
        </div>

        {error && <div className="sdkp-notice sdkp-error">Failed to load SDK performance data from Quality Hub.</div>}
        {!loading && performance?.ios?.message && selectedPlatform === 'ios' && (
          <div className="sdkp-notice">{performance.ios.message}</div>
        )}
        {loading && <div className="sdkp-empty">Loading…</div>}

        {!loading && (
          <>
            <RunContext run={latestRun} summary={latestSummary} deviceCount={platformDeviceCount} />
            <Takeaway rows={rows} platform={selectedPlatform} />
            <div className="sdkp-kpis">
              {rows.map(({ kpi, value, range }) => {
                const kpiDevices = figureDevices(kpi, latestSummary);
                return (
                  <KpiCard
                    key={kpi.key}
                    kpi={kpi}
                    platform={selectedPlatform}
                    value={value}
                    range={range}
                    figure={kpi.metric ? latestSummary?.figures?.[kpi.metric] : null}
                    devices={kpiDevices}
                    onlyOfMany={selectedDevice === DEVICE_ALL && kpiDevices.length === 1 && platformDeviceCount > 1}
                    conclusion={latestRun?.conclusion}
                    notMeasured={notMeasuredOnDevice(kpi.reports)}
                    active={kpi.key === trendKpi.key}
                  />
                );
              })}
            </div>
          </>
        )}

        <CostTrend
          entries={entries}
          allRuns={history}
          kpis={kpis}
          kpi={trendKpi}
          platform={selectedPlatform}
          onSelect={setTrendKey}
          showRaw={showRaw}
          onShowRaw={setShowRaw}
          loading={loading}
        />

        <SessionReplayTrend key={selectedPlatform} history={history} metrics={metrics} selectedPlatform={selectedPlatform} selectedDevice={selectedDevice} selectedSdkVersion={selectedSdkVersion} loading={loading} />

        <div className="sdkp-panel" data-panel="runs">
          <h2 className="sdkp-panel-title">Nightly runs</h2>
          {loading ? <div className="sdkp-empty">Loading…</div> : <RunHistory reportedRuns={reportedRuns} summaries={summaries} kpis={kpis} platform={selectedPlatform} />}
        </div>
      </div>
    </div>
  );
}

export default SdkPerformance;
